import { useState, useEffect, useCallback, useRef } from 'react';
import { Html5QrcodeScanner, Html5QrcodeScanType } from 'html5-qrcode';
import { useAuth } from '@/context/AuthContext';
import { usePermissions } from '@/hooks/usePermissions';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import { BadgePill } from '@/components/campus';
import {
  QrCode,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Search,
  Users,
  Loader2,
  Camera,
  CameraOff,
  RefreshCw,
  UserCheck,
  Clock,
} from 'lucide-react';
import { cn } from '@/lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// ==================== SCAN RESULT DISPLAY ====================
const ScanResultDisplay = ({ result, onClear }) => {
  // Auto-clear after 4 seconds
  useEffect(() => {
    if (result) {
      const timer = setTimeout(onClear, 4000);
      return () => clearTimeout(timer);
    }
  }, [result, onClear]);

  // Play sound/vibrate on result
  useEffect(() => {
    if (result && 'vibrate' in navigator) {
      if (result.result === 'success') {
        navigator.vibrate(100);
      } else {
        navigator.vibrate([100, 50, 100]);
      }
    }
  }, [result]);

  if (!result) return null;

  const configs = {
    success: {
      bg: 'bg-cool-mint/20',
      border: 'border-cool-mint/40',
      icon: CheckCircle,
      iconColor: 'text-cool-mint',
      title: 'APPROVED',
      titleColor: 'text-cool-mint',
    },
    already_used: {
      bg: 'bg-soft-amber/20',
      border: 'border-soft-amber/40',
      icon: AlertTriangle,
      iconColor: 'text-soft-amber',
      title: 'ALREADY USED',
      titleColor: 'text-soft-amber',
    },
    invalid: {
      bg: 'bg-signal-red/20',
      border: 'border-signal-red/40',
      icon: XCircle,
      iconColor: 'text-signal-red',
      title: 'INVALID',
      titleColor: 'text-signal-red',
    },
    revoked: {
      bg: 'bg-signal-red/20',
      border: 'border-signal-red/40',
      icon: XCircle,
      iconColor: 'text-signal-red',
      title: 'REVOKED',
      titleColor: 'text-signal-red',
    },
    wrong_event: {
      bg: 'bg-signal-red/20',
      border: 'border-signal-red/40',
      icon: XCircle,
      iconColor: 'text-signal-red',
      title: 'WRONG EVENT',
      titleColor: 'text-signal-red',
    },
  };

  const config = configs[result.result] || configs.invalid;
  const Icon = config.icon;

  return (
    <div
      className={cn(
        'absolute inset-0 z-50 flex flex-col items-center justify-center p-6',
        config.bg,
        'border-4',
        config.border,
        'rounded-2xl animate-in fade-in zoom-in-95 duration-200'
      )}
      onClick={onClear}
      data-testid="scan-result-display"
    >
      <Icon className={cn('w-24 h-24 mb-4', config.iconColor)} strokeWidth={2.5} />
      <h2 className={cn('text-4xl font-bold mb-2 font-mono tracking-wider', config.titleColor)}>
        {config.title}
      </h2>
      {result.guest_name && (
        <p className="text-2xl text-cloud-white font-semibold mb-2">{result.guest_name}</p>
      )}
      <p className="text-soft-silver text-center text-lg">{result.message}</p>
      {result.checked_in_at && result.result === 'already_used' && (
        <p className="text-muted-text font-mono text-sm mt-2">
          Scanned at: {new Date(result.checked_in_at).toLocaleTimeString()}
        </p>
      )}
      <p className="text-muted-text text-xs mt-4">Tap to dismiss</p>
    </div>
  );
};

// ==================== LIVE STATS BAR ====================
const LiveStatsBar = ({ stats, loading }) => {
  return (
    <div 
      className="fixed bottom-0 left-0 right-0 z-40 bg-graphite/95 backdrop-blur-lg border-t border-white/[0.08] px-4 py-3 safe-area-bottom"
      data-testid="live-stats-bar"
    >
      <div className="max-w-lg mx-auto flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-electric-ice-blue/10 flex items-center justify-center">
            <Users className="w-5 h-5 text-electric-ice-blue" />
          </div>
          <div>
            <p className="text-xs text-muted-text uppercase tracking-wider">Checked In</p>
            <p className="text-xl font-bold text-cloud-white font-mono">
              {loading ? (
                <Loader2 className="w-5 h-5 animate-spin inline" />
              ) : (
                <>
                  {stats?.checked_in || 0}
                  <span className="text-muted-text font-normal"> / </span>
                  {stats?.capacity || 0}
                </>
              )}
            </p>
          </div>
        </div>
        
        <div className="flex items-center gap-4">
          <div className="text-right">
            <p className="text-xs text-muted-text uppercase tracking-wider">Remaining</p>
            <p className="text-lg font-semibold text-cool-mint font-mono">
              {stats?.remaining || 0}
            </p>
          </div>
          <div className="h-12 w-12 rounded-full bg-obsidian border border-white/[0.08] flex items-center justify-center">
            <span className="text-sm font-bold text-electric-ice-blue font-mono">
              {stats?.utilization_percent || 0}%
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

// ==================== MANUAL SEARCH ====================
const ManualSearch = ({ eventId, session, onCheckIn }) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [checkingIn, setCheckingIn] = useState(null);

  const handleSearch = async () => {
    if (!query.trim() || !eventId) return;
    
    setSearching(true);
    try {
      const res = await fetch(
        `${API_URL}/api/guests?event_id=${eventId}&page_size=20`,
        { headers: { Authorization: `Bearer ${session?.access_token}` } }
      );
      
      if (res.ok) {
        const data = await res.json();
        // Client-side filter by search query
        const filtered = (data.guests || []).filter(g => 
          g.guest_name?.toLowerCase().includes(query.toLowerCase()) ||
          g.email?.toLowerCase().includes(query.toLowerCase()) ||
          g.phone?.includes(query)
        );
        setResults(filtered);
      }
    } catch (error) {
      toast.error('Search failed');
    } finally {
      setSearching(false);
    }
  };

  const handleManualCheckIn = async (guest) => {
    if (!guest.has_pass) {
      toast.error('Guest does not have a pass');
      return;
    }
    
    setCheckingIn(guest.id);
    try {
      // First get the guest's pass
      const passRes = await fetch(
        `${API_URL}/api/passes?event_id=${eventId}`,
        { headers: { Authorization: `Bearer ${session?.access_token}` } }
      );
      
      if (passRes.ok) {
        const passData = await passRes.json();
        const guestPass = passData.passes?.find(p => p.guest_id === guest.id && p.status === 'valid');
        
        if (guestPass) {
          // Scan the pass
          const scanRes = await fetch(`${API_URL}/api/scanner/scan`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${session?.access_token}`,
            },
            body: JSON.stringify({
              qr_code_data: guestPass.qr_code_data,
              event_id: eventId,
            }),
          });
          
          if (scanRes.ok) {
            const result = await scanRes.json();
            toast.success(`${guest.guest_name} checked in!`);
            onCheckIn?.(result);
            // Refresh results
            handleSearch();
          }
        } else {
          toast.error('Valid pass not found');
        }
      }
    } catch (error) {
      toast.error('Check-in failed');
    } finally {
      setCheckingIn(null);
    }
  };

  return (
    <div className="space-y-4" data-testid="manual-search">
      <div className="flex gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-text" />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
            placeholder="Search by name, email, or phone..."
            className="pl-10 bg-obsidian border-white/[0.08] text-cloud-white"
            data-testid="manual-search-input"
          />
        </div>
        <Button
          onClick={handleSearch}
          disabled={searching || !query.trim()}
          className="bg-electric-ice-blue/10 text-electric-ice-blue border border-electric-ice-blue/20"
        >
          {searching ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Search'}
        </Button>
      </div>

      {results.length > 0 && (
        <div className="space-y-2 max-h-64 overflow-y-auto">
          {results.map(guest => (
            <div
              key={guest.id}
              className="flex items-center justify-between p-3 bg-obsidian/50 rounded-lg border border-white/[0.06]"
            >
              <div className="flex-1 min-w-0">
                <p className="font-medium text-cloud-white truncate">{guest.guest_name}</p>
                <div className="flex items-center gap-2 mt-1">
                  <BadgePill variant={guest.approval_status === 'approved' ? 'success' : 'default'} size="sm">
                    {guest.approval_status?.toUpperCase()}
                  </BadgePill>
                  {guest.has_pass && (
                    <BadgePill variant="primary" size="sm">HAS PASS</BadgePill>
                  )}
                  {guest.checked_in && (
                    <BadgePill variant="success" size="sm">CHECKED IN</BadgePill>
                  )}
                </div>
              </div>
              {guest.approval_status === 'approved' && guest.has_pass && !guest.checked_in && (
                <Button
                  size="sm"
                  onClick={() => handleManualCheckIn(guest)}
                  disabled={checkingIn === guest.id}
                  className="bg-cool-mint text-obsidian hover:bg-cool-mint/90"
                  data-testid={`manual-checkin-${guest.id}`}
                >
                  {checkingIn === guest.id ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <>
                      <UserCheck className="w-4 h-4 mr-1" />
                      Check In
                    </>
                  )}
                </Button>
              )}
            </div>
          ))}
        </div>
      )}

      {results.length === 0 && query && !searching && (
        <p className="text-center text-muted-text text-sm py-4">No guests found</p>
      )}
    </div>
  );
};

// ==================== QR SCANNER COMPONENT ====================
const QRScannerView = ({ onScan, isScanning, setIsScanning }) => {
  const scannerRef = useRef(null);
  const scannerContainerRef = useRef(null);

  useEffect(() => {
    if (!isScanning || !scannerContainerRef.current) return;

    // Initialize scanner
    scannerRef.current = new Html5QrcodeScanner(
      'qr-scanner-container',
      {
        fps: 10,
        qrbox: { width: 250, height: 250 },
        aspectRatio: 1,
        supportedScanTypes: [Html5QrcodeScanType.SCAN_TYPE_CAMERA],
        rememberLastUsedCamera: true,
        showTorchButtonIfSupported: true,
      },
      false
    );

    scannerRef.current.render(
      (decodedText) => {
        // Success callback
        onScan(decodedText);
      },
      (error) => {
        // Error callback - ignore continuous scanning errors
        // console.log('QR scan error:', error);
      }
    );

    return () => {
      if (scannerRef.current) {
        scannerRef.current.clear().catch(() => {});
      }
    };
  }, [isScanning, onScan]);

  return (
    <div className="relative">
      {isScanning ? (
        <div ref={scannerContainerRef} className="relative">
          <div 
            id="qr-scanner-container" 
            className="rounded-xl overflow-hidden"
            data-testid="qr-scanner-container"
          />
          <Button
            onClick={() => setIsScanning(false)}
            variant="outline"
            className="absolute top-2 right-2 z-10 bg-obsidian/80 border-white/[0.08]"
          >
            <CameraOff className="w-4 h-4 mr-2" />
            Stop
          </Button>
        </div>
      ) : (
        <div 
          className="aspect-square max-w-sm mx-auto bg-obsidian/50 rounded-xl border-2 border-dashed border-white/[0.12] flex flex-col items-center justify-center gap-4 cursor-pointer hover:border-electric-ice-blue/40 transition-colors"
          onClick={() => setIsScanning(true)}
          data-testid="start-scanner-btn"
        >
          <div className="w-20 h-20 rounded-2xl bg-electric-ice-blue/10 flex items-center justify-center">
            <Camera className="w-10 h-10 text-electric-ice-blue" />
          </div>
          <div className="text-center">
            <p className="text-cloud-white font-medium">Tap to Start Scanner</p>
            <p className="text-sm text-muted-text mt-1">Point camera at QR code</p>
          </div>
        </div>
      )}
    </div>
  );
};

// ==================== MAIN SCANNER PAGE ====================
export const Scanner = () => {
  const { organization, session } = useAuth();
  const { isDoorStaff } = usePermissions();
  
  // State
  const [events, setEvents] = useState([]);
  const [selectedEventId, setSelectedEventId] = useState('');
  const [loading, setLoading] = useState(true);
  const [isScanning, setIsScanning] = useState(false);
  const [scanResult, setScanResult] = useState(null);
  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(false);
  const [showManualSearch, setShowManualSearch] = useState(false);
  const [processing, setProcessing] = useState(false);

  // Fetch events
  const fetchEvents = useCallback(async () => {
    if (!organization?.id || !session?.access_token) {
      setLoading(false);
      return;
    }

    try {
      const res = await fetch(
        `${API_URL}/api/events?organization_id=${organization.id}&page_size=100`,
        { headers: { Authorization: `Bearer ${session.access_token}` } }
      );

      if (res.ok) {
        const data = await res.json();
        const eventList = data.events || [];
        setEvents(eventList);
        
        // Auto-select tonight's event or the most recent upcoming one
        if (eventList.length > 0 && !selectedEventId) {
          const now = new Date();
          const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
          const tomorrow = new Date(today.getTime() + 24 * 60 * 60 * 1000);
          
          // Find today's event
          const todayEvent = eventList.find(e => {
            const eventDate = new Date(e.start_time);
            return eventDate >= today && eventDate < tomorrow;
          });
          
          // Or find the nearest upcoming event
          const upcomingEvents = eventList
            .filter(e => new Date(e.start_time) >= now)
            .sort((a, b) => new Date(a.start_time) - new Date(b.start_time));
          
          const defaultEvent = todayEvent || upcomingEvents[0] || eventList[0];
          if (defaultEvent) {
            setSelectedEventId(defaultEvent.id);
          }
        }
      }
    } catch (error) {
      console.error('Failed to fetch events:', error);
    } finally {
      setLoading(false);
    }
  }, [organization?.id, session?.access_token, selectedEventId]);

  // Fetch scanner stats
  const fetchStats = useCallback(async () => {
    if (!selectedEventId || !session?.access_token) return;

    setStatsLoading(true);
    try {
      const res = await fetch(
        `${API_URL}/api/scanner/stats?event_id=${selectedEventId}`,
        { headers: { Authorization: `Bearer ${session.access_token}` } }
      );

      if (res.ok) {
        const data = await res.json();
        setStats(data);
      }
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    } finally {
      setStatsLoading(false);
    }
  }, [selectedEventId, session?.access_token]);

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  useEffect(() => {
    if (selectedEventId) {
      fetchStats();
      // Poll stats every 10 seconds
      const interval = setInterval(fetchStats, 10000);
      return () => clearInterval(interval);
    }
  }, [selectedEventId, fetchStats]);

  // Handle QR scan
  const handleQRScan = useCallback(async (qrCodeData) => {
    if (processing || !selectedEventId || !session?.access_token) return;

    setProcessing(true);
    try {
      const res = await fetch(`${API_URL}/api/scanner/scan`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({
          qr_code_data: qrCodeData,
          event_id: selectedEventId,
        }),
      });

      if (res.ok) {
        const result = await res.json();
        setScanResult(result);
        
        // Refresh stats after successful check-in
        if (result.result === 'success') {
          fetchStats();
        }
      } else {
        const error = await res.json();
        setScanResult({
          result: 'invalid',
          message: error.detail || 'Scan failed',
          guest_name: null,
        });
      }
    } catch (error) {
      setScanResult({
        result: 'invalid',
        message: 'Network error - please try again',
        guest_name: null,
      });
    } finally {
      setProcessing(false);
    }
  }, [processing, selectedEventId, session?.access_token, fetchStats]);

  // Clear scan result
  const clearScanResult = useCallback(() => {
    setScanResult(null);
  }, []);

  // Handle manual check-in result
  const handleManualCheckIn = useCallback(() => {
    fetchStats();
  }, [fetchStats]);

  // Get selected event
  const selectedEvent = events.find(e => e.id === selectedEventId);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="w-8 h-8 text-electric-ice-blue animate-spin" />
          <p className="text-soft-silver text-sm">Loading scanner...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pb-24" data-testid="scanner-page">
      {/* Header */}
      <div className="sticky top-0 z-30 bg-obsidian/95 backdrop-blur-lg border-b border-white/[0.08] px-4 py-3">
        <div className="max-w-lg mx-auto">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-electric-ice-blue/10 flex items-center justify-center">
                <QrCode className="w-5 h-5 text-electric-ice-blue" />
              </div>
              <div>
                <h1 className="text-lg font-semibold text-cloud-white">Door Scanner</h1>
                <p className="text-xs text-muted-text">Check in guests</p>
              </div>
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={fetchStats}
              className="text-muted-text hover:text-cloud-white"
            >
              <RefreshCw className={cn('w-4 h-4', statsLoading && 'animate-spin')} />
            </Button>
          </div>

          {/* Event Selector */}
          <Select value={selectedEventId} onValueChange={setSelectedEventId}>
            <SelectTrigger 
              className="bg-graphite border-white/[0.08] text-cloud-white"
              data-testid="event-selector"
            >
              <SelectValue placeholder="Select event to scan" />
            </SelectTrigger>
            <SelectContent className="bg-graphite border-white/[0.08]">
              {events.map(event => (
                <SelectItem 
                  key={event.id} 
                  value={event.id}
                  className="text-cloud-white hover:bg-white/[0.05]"
                >
                  <div className="flex items-center gap-2">
                    <span>{event.event_name}</span>
                    <span className="text-xs text-muted-text font-mono">
                      {new Date(event.start_time).toLocaleDateString()}
                    </span>
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-lg mx-auto px-4 py-6 space-y-6">
        {!selectedEventId ? (
          <div className="text-center py-12">
            <QrCode className="w-16 h-16 text-muted-text mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-cloud-white mb-2">Select an Event</h2>
            <p className="text-soft-silver">Choose an event above to start scanning</p>
          </div>
        ) : (
          <>
            {/* Event Info */}
            {selectedEvent && (
              <div className="p-4 bg-graphite/50 rounded-xl border border-white/[0.06]">
                <h2 className="text-lg font-semibold text-cloud-white">{selectedEvent.event_name}</h2>
                <div className="flex items-center gap-4 mt-2 text-sm text-soft-silver">
                  <div className="flex items-center gap-1">
                    <Clock className="w-4 h-4" />
                    <span className="font-mono">
                      {new Date(selectedEvent.start_time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </span>
                  </div>
                  {selectedEvent.location && (
                    <span className="truncate">{selectedEvent.location}</span>
                  )}
                </div>
              </div>
            )}

            {/* Scanner Area */}
            <div className="relative">
              <QRScannerView 
                onScan={handleQRScan}
                isScanning={isScanning}
                setIsScanning={setIsScanning}
              />
              
              {/* Scan Result Overlay */}
              {scanResult && (
                <ScanResultDisplay result={scanResult} onClear={clearScanResult} />
              )}
              
              {/* Processing Overlay */}
              {processing && (
                <div className="absolute inset-0 bg-obsidian/80 flex items-center justify-center rounded-xl">
                  <div className="flex flex-col items-center gap-2">
                    <Loader2 className="w-8 h-8 text-electric-ice-blue animate-spin" />
                    <p className="text-soft-silver text-sm">Processing...</p>
                  </div>
                </div>
              )}
            </div>

            {/* Toggle Manual Search */}
            <Button
              variant="outline"
              onClick={() => setShowManualSearch(!showManualSearch)}
              className="w-full border-white/[0.08] text-soft-silver hover:text-cloud-white"
              data-testid="toggle-manual-search"
            >
              <Search className="w-4 h-4 mr-2" />
              {showManualSearch ? 'Hide Manual Search' : 'Manual Search'}
            </Button>

            {/* Manual Search */}
            {showManualSearch && (
              <ManualSearch 
                eventId={selectedEventId}
                session={session}
                onCheckIn={handleManualCheckIn}
              />
            )}

            {/* Recent Check-ins */}
            {stats?.checked_in > 0 && (
              <div className="text-center text-sm text-muted-text">
                <span className="font-mono text-cool-mint">{stats.checked_in}</span> guests checked in
              </div>
            )}
          </>
        )}
      </div>

      {/* Live Stats Bar */}
      {selectedEventId && (
        <LiveStatsBar stats={stats} loading={statsLoading} />
      )}
    </div>
  );
};

export default Scanner;
